import { chain } from '@leprecoins/effector';
import { createForm } from '@leprecoins/effector-forms';
import { createEvent, createStore, sample } from 'effector';
import { or } from 'patronum';
import { createShiftRegistryModel, shiftModel } from 'entities/shift';
import { viewerModel } from 'entities/viewer';
import {
	createStartShiftMutation,
	createStopShiftMutation,
	getShiftQuery,
	receiveShiftClosed,
	receiveShiftOpened,
	receiveShiftPersonal,
	receiveShiftTotal,
	ShiftTotal,
} from 'shared/api';
import { createState, notify } from 'shared/lib';
import { authorizedShiftsRoute } from 'shared/routing';

const shiftRegistryModel = createShiftRegistryModel();

const startShiftMutation = createStartShiftMutation();

const stopShiftMutation = createStopShiftMutation();

const form = createForm({
	fields: {
		balance: {
			init: 0,
		},
	},
});

const $shift = createStore<ShiftTotal | null>(null);

const $pending = or(viewerModel.getCashdeskQuery.$pending, getShiftQuery.$pending);

const $isEmpty = viewerModel.$profile.map(
	(profile) => profile === null || profile.cashdeskId === null,
);

const $isPrevShift = shiftModel.$shiftTotal.map((shift) => !shift?.isActive);

const isPersonal = createState(false);

const $currency = viewerModel.$cashdesk.map((cashdesk) => cashdesk?.currency ?? null);

const stopButtonClicked = createEvent();

chain(authorizedShiftsRoute.opened, getShiftQuery.refresh);

sample({
	clock: [startShiftMutation.finished.success, receiveShiftOpened.success],
	target: form.reset,
});

sample({
	clock: [
		stopShiftMutation.finished.success.map(({ result }) => result),
		getShiftQuery.finished.success.map(({ result }) => result),
		startShiftMutation.finished.success.map(({ result }) => result),
	],
	target: [shiftModel.$shiftTotal, shiftModel.$shiftPersonal],
});

sample({
	clock: [receiveShiftOpened.success, receiveShiftClosed.success, receiveShiftTotal.success],
	target: shiftModel.$shiftTotal,
});

chain(receiveShiftPersonal.success, shiftModel.$shiftPersonal);

sample({
	clock: form.submitted,
	source: form.$values,
	target: startShiftMutation.start,
});

sample({
	clock: stopButtonClicked,
	source: shiftModel.$shiftTotal,
	filter: Boolean,
	fn: ({ id }) => id,
	target: stopShiftMutation.start,
});

sample({
	clock: [startShiftMutation.finished.success, receiveShiftOpened.success],
	target: shiftModel.activatedGameRoute.open,
});

sample({
	clock: [isPersonal.$value, shiftModel.$shiftTotal, shiftModel.$shiftPersonal],
	source: {
		total: shiftModel.$shiftTotal,
		personal: shiftModel.$shiftPersonal,
		is: isPersonal.$value,
	},
	fn: ({ total, personal, is }) => {
		if (is && personal)
			return {
				...personal,
				totalDeposits: personal.deposits,
				totalWithdrawals: personal.withdrawals,
				totalIncome: personal.income,
			};
		if (!is && total) return total;

		return null;
	},
	target: $shift,
});

chain(receiveShiftClosed.success, authorizedShiftsRoute.open);

notify(stopShiftMutation.finished.success, 'shifts-page.end-shift-success');

notify(startShiftMutation.finished.success, 'shifts-page.start-shift-success');

export const shiftsPageModel = {
	shiftRegistryModel,
	startShiftMutation,
	stopShiftMutation,
	$currency,
	$isEmpty,
	$pending,
	$isPrevShift,
	$shift,
	isPersonal,
	form,
	openingBalanceField: form.fields.balance,
	stopButtonClicked,
};
